import { Component, OnInit, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';

import { ProfileDto } from '../questions/models/profile-dto';
import {
  ABP,
  ConfigStateService,
  ListService,
  PagedResultDto,
  SubscriptionService,
  TrackByService,
} from '@abp/ng.core';
import { finalize } from 'rxjs/operators';
import { eThemeLeptonComponents } from '@volo/abp.ng.theme.lepton';
import { AccountService, ManageProfileStateService } from '@volo/abp.ng.account/public';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ProfileService } from '@volo/abp.ng.account/public/proxy';
import { Subscription } from 'rxjs';
import { transition, trigger, useAnimation } from '@angular/animations';
import { fadeIn } from '@abp/ng.theme.shared';
import { twoFactorBehaviourOptions } from '@volo/abp.ng.account/admin';
import { FormGroup } from '@angular/forms';
import {
  GetQuestionsInput,
  QuestionDto,
  QuestionService,
  questionStatusOptions,
} from '@proxy/questions';
import { NgbDateAdapter, NgbDateNativeAdapter } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { QuestionCardComponent } from '../questions/components/question-card/question-card.component';
import { IdentityUserDto, UserServiceProxy } from '@proxy/shared';

@Component({
  selector: 'lib-my-questions',
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './my-questions.component.html',
  styleUrls: ['./my-questions.component.scss'],
  providers: [ListService, { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter }],
  animations: [trigger('fadeIn', [transition(':enter', useAnimation(fadeIn))])],
})
export class MyQuestionsComponent implements OnInit, OnChanges {
  @Input() profile = {} as ProfileDto;
  profileLoaded: boolean = false;
  isProfileLoaded: boolean;

  isModalBusy = false;
  isModalOpen = false;

  isQuestionModalBusy = false;
  isQuestionModalOpen = false;

  isAskQuestionModalBusy = false;
  isAskQuestionModalOpen = false;

  profilePicture: string | ArrayBuffer | null | undefined = '';
  profilePictureLoaded: boolean = false;
  hasCustomProfilePicture: boolean = false;

  currentUserImageComponentKey = eThemeLeptonComponents.CurrentUserImage;
  currentUser = this.configState.getOne('currentUser');
  identityUserDto = {} as IdentityUserDto;

  subscription: Subscription;

  form: FormGroup;
  selectedQuestion = {} as QuestionDto;

  data: PagedResultDto<QuestionDto> = {
    items: [],
    totalCount: 0,
  };
  filters = {} as GetQuestionsInput;
  isFiltersHidden = true;

  questionStatusOptions = questionStatusOptions;
  page = 5;

  get isTwoFactorEnabled(): boolean {
    const { key } = twoFactorBehaviourOptions[0];
    return (
      this.configState.getFeature('Identity.TwoFactor') === key &&
      this.configState.getSetting('Abp.Identity.TwoFactor.Behaviour') === key &&
      (
        (this.configState.getSetting('Abp.Identity.TwoFactor.UsersCanChange') as string) || ''
      ).toLowerCase() === 'true'
    );
  }

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: QuestionService,
    protected profileService: ProfileService,
    protected manageProfileState: ManageProfileStateService,
    private userService: UserServiceProxy,
    private configState: ConfigStateService,
    private accountService: AccountService,
    private sanitizer: DomSanitizer,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.getUser();
    this.profileService.get().subscribe(profile => {
      this.manageProfileState.setProfile(profile);
      this.profile.isExternal = profile.isExternal;
      this.isProfileLoaded = true;
    });
  }

  ngOnChanges(): void {
    this.getUser();
  }

  askQuestion() {
    this.isAskQuestionModalOpen = true;
  }

  populateMyQuestions(openedBy) {
    this.filters.openedBy = openedBy;

    this.list.maxResultCount = this.page;
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<QuestionDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  getUser() {
    if (this.currentUser.id != undefined) {
      if (!this.profileLoaded) {
        this.userService
          .includeDeleted(this.currentUser.id)
          .pipe(finalize(() => this.okProfile()))
          .subscribe(identityUser => (this.identityUserDto = identityUser));
      }

      if (!this.profilePictureLoaded) {
        this.accountService
          .getProfilePicture(this.currentUser.id)
          .pipe(finalize(() => this.okProfilePicture()))
          .subscribe((blob: any) => {
            let objectUrl = 'data:image/jpg;base64,' + blob.fileContent;
            this.profile.picture = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
          });
      }
    }
  }

  onShowMore() {
    this.list.maxResultCount = this.list.maxResultCount + this.page;
  }

  viewQuestion(item) {
    this.selectedQuestion = item;
    this.isQuestionModalOpen = true;
  }

  isCurrent() {
    if (this.profile.userId === this.currentUser.id) {
      return 'highlight';
    }
    return '';
  }
  hideForm() {
    this.isModalOpen = false;
  }

  showForm() {
    this.isModalOpen = true;
  }
  private okProfile() {
    if (this.identityUserDto.surname == null) {
      this.identityUserDto.surname = '';
    }

    if (this.identityUserDto.name == null) {
      this.identityUserDto.name = '';
    }

    this.profile.user = this.identityUserDto.name + ' ' + this.identityUserDto.surname;
    this.profile.name = this.identityUserDto.name;
    this.profile.surname = this.identityUserDto.surname;

    this.populateMyQuestions(this.identityUserDto.id);

    this.profileLoaded = true;
  }

  private okProfilePicture() {
    if (
      this.profile.picture.changingThisBreaksApplicationSecurity ==
      'data:image/jpg;base64,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'
    ) {
      this.hasCustomProfilePicture = false;
    } else {
      this.hasCustomProfilePicture = true;
    }
    this.profilePictureLoaded = true;
  }
}
